import styles from "../TeamSwitcher/TeamSwitcher.module.css";
import posthogEvents from "@app/config/posthog/posthog-events";
import { useGlobalStore } from "@app/state/store";
import { useAuth0 } from "@auth0/auth0-react";
import type { Organization } from "@globals";
import Icon from "@lib/common/components/Icon/Icon";
import invariant from "@lib/utils/invariant";
import { Avatar, Card, Flex, Group, ScrollArea, Stack, Text, Title, UnstyledButton } from "@mantine/core";
import { usePostHog } from "posthog-js/react";

type Props = {
  onOrganizationSelection: (organization: Organization) => void;
};

export default function OrganizationSwitcher({ onOrganizationSelection }: Props) {
  const user = useGlobalStore(state => state.user);

  invariant(user, "User must be defined to render OrganizationSwitcher");

  const { loginWithRedirect } = useAuth0();
  const posthog = usePostHog();

  const handleOrganizationSelect = async (organization: Organization) => {
    posthog.capture(posthogEvents.organization_switcher.organization_click, { organization: organization.name });
    onOrganizationSelection(organization);

    if (organization.id === user.current_organization.id) {
      return;
    }

    await loginWithRedirect({
      authorizationParams: { organization: organization.id },
      appState: { returnTo: "/" }
    });
  };

  return (
    <Stack gap={0}>
      <Title order={5}>Switch organization</Title>
      <Stack gap="lg" py="sm">
        <ScrollArea.Autosize mah="80vh">
          <Stack gap="sm">
            {user.organizations.map(organization => {
              const isSelected = organization.id === user.current_organization.id;

              return (
                <UnstyledButton key={organization.id} w="100%" onClick={() => handleOrganizationSelect(organization)}>
                  <Card className={styles.cardRoot} p="xs" data-selected={isSelected || undefined}>
                    <Group justify="space-between" align="center" wrap="nowrap">
                      <Group align="center" wrap="nowrap">
                        <Avatar color="initials" name={organization.name ?? ""} radius="md">
                          <Icon name="buildings" variant="light" />
                        </Avatar>
                        <Text fw={600} truncate maw={290}>
                          {organization.name}
                        </Text>
                      </Group>
                      {isSelected && <Icon name="check" />}
                    </Group>
                  </Card>
                </UnstyledButton>
              );
            })}
          </Stack>
        </ScrollArea.Autosize>

        <Group wrap="nowrap" align="flex-start" gap="xs">
          <Flex mt={2}>
            <Icon name="home" variant="light" color="dimmed" size="0.9em" />
          </Flex>
          <Text size="xs" c="dimmed">
            Changing organization signs you in again and redirects you to the <b>Home</b> page.
          </Text>
        </Group>
      </Stack>
    </Stack>
  );
}
